import {
    AfterViewInit,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import * as d3 from 'd3';
import {Color} from 'd3';

export type GroupedBarData = {
    labels: string[];                 // x-axis group names
    datasets: { label: string; data: number[] }[]; // series with same length as labels
};

@Component({
    selector: 'lib-bar-chart',
    templateUrl: './bar-chart.component.html',
    styleUrls: ['./bar-chart.component.scss'],
    encapsulation: ViewEncapsulation.Emulated

})
export class BarChartComponent implements AfterViewInit, OnChanges, OnDestroy {
    @ViewChild('svg', {static: true}) private svgRef!: ElementRef<SVGSVGElement>;

    @Input() data: GroupedBarData = {labels: [], datasets: []};

    @Input() height = 420;
    @Input() marginTop = 20;
    @Input() marginRight = 30;
    @Input() marginBottom = 40;
    @Input() marginLeft = 40;

    @Input() groupPadding = 0.1;
    @Input() seriesPadding = 0.05;
    @Input() colors: readonly string[] | null = null;
    @Input() yTickFormat: any = '';

    private ro?: ResizeObserver;

    constructor(private host: ElementRef<HTMLElement>) {
    }

    ngAfterViewInit(): void {
        this.observeResize();
        this.render();
    }

    ngOnChanges(): void {
        this.render();
    }

    ngOnDestroy(): void {
        this.ro?.disconnect();
    }

    private observeResize() {
        const el = this.host.nativeElement.querySelector('.gbar-wrapper') as HTMLElement;
        if (!el || typeof ResizeObserver === 'undefined') {
            return;
        }
        this.ro?.disconnect();
        this.ro = new ResizeObserver(() => this.render());
        this.ro.observe(el);
    }

    private render() {
        const svgEl = this.svgRef?.nativeElement;
        if (!svgEl) {
            return;
        }

        const labels = this.data?.labels ?? [];
        const datasets = this.data?.datasets ?? [];

        if (!labels.length || !datasets.length || !datasets.every(ds => ds.data.length === labels.length)) {
            svgEl.replaceChildren();
            return;
        }

        const container = this.host.nativeElement.querySelector('.gbar-wrapper') as HTMLElement;
        const width = Math.max(320, container?.clientWidth || 800);

        const flat: { group: string; key: string; value: number }[] = [];
        labels.forEach((g, i) => {
            datasets.forEach(ds => {
                flat.push({group: g, key: ds.label, value: +ds.data[i]});
            });
        });
        const yMax = d3.max(flat, d => d.value) ?? 0;
        const seriesKeys = datasets.map(d => d.label);
        // const yMax = d3.max(flat, (d: any) => d.value) ?? 0;

        const x0 = d3.scaleBand<string>()
            .domain(labels)
            .range([this.marginLeft, width - this.marginRight])
            .paddingInner(this.groupPadding);

        const x1 = d3.scaleBand<string>()
            .domain(seriesKeys)
            .range([0, x0.bandwidth()])
            .padding(this.seriesPadding);

        const y = d3.scaleLinear()
            .domain([0, yMax]).nice()
            .range([this.height - this.marginBottom, this.marginTop]);

        // const color = d3.scaleOrdinal<string, string>()
        //     .domain(seriesKeys)
        //     .range((this.colors && this.colors.length >= seriesKeys.length
        //         ? this.colors
        //         : (d3.schemeTableau10 as unknown as string[])));

        const getColor = (prop: any): any => {

            if (prop.key.toLowerCase() === 'ukraine') {
                return 'rgba(0,0,0,.08)';
            }
            switch (prop.group) {
                case ('Low'):
                    return '#fff649';
                case ('Optimal'):
                    return '#0fff7e';
                case ('High'):
                    return '#fe5852';
                case ('Minimal'):
                    return '#0fff7e';
                case ('Moderate'):
                    return '#fff649';
                case ('Severe'):
                    return '#fe5852';
            }
        };

        const svg = d3.select(svgEl)
            .attr('viewBox', `0 0 ${width} ${this.height}`)
            .attr('width', width)
            .attr('height', this.height);

        svg.selectAll('*').remove();

        const xAxis = (g: d3.Selection<SVGGElement, unknown, any, unknown>) => {
            g.attr('transform', `translate(0,${this.height - this.marginBottom})`)
                .call(d3.axisBottom(x0).tickSizeOuter(0))
                .call(s => s.selectAll('text').attr('dy', '1em'));
        };

        const yAxis = (g: d3.Selection<SVGGElement, unknown, any, unknown>) => {
            g.attr('transform', `translate(${this.marginLeft},0)`)
                .call(d3.axisLeft(y).ticks(Math.min(10, yMax)).tickFormat(this.yTickFormat as any))
                .attr('stroke-opacity', 0.08)

                .call(s => s.select('.domain').remove())
                .call(s => s.selectAll('.tick line').clone()
                    .attr('x2', width - this.marginLeft - this.marginRight)
                    .attr('stroke-opacity', 0.08)
                );
        };

        svg.append('g').attr('class', 'axis axis--x').call(xAxis);
        svg.append('g').attr('class', 'axis axis--y').call(yAxis);

        const groups = svg.append('g')
            .selectAll('g')
            .data(labels)
            .join('g')
            .attr('transform', g => `translate(${x0(g)},0)`);

        groups.selectAll('rect')
            .data(g => datasets.map(ds => ({
                group: g,
                key: ds.label,
                value: +ds.data[labels.indexOf(g)]
            })))
            .join('rect')
            .attr('class', 'bar')
            // tslint:disable-next-line:no-non-null-assertion
            .attr('x', d => x1(d.key)!)
            .attr('y', d => y(d.value))
            .attr('width', x1.bandwidth())
            .attr('height', d => y(0) - y(d.value))
            .attr('fill', d => getColor(d));
    }
}
